import { useLayoutEffect, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "../../hooks/usePrefersReducedMotion.js";

gsap.registerPlugin(ScrollTrigger);

export { gsap, ScrollTrigger };

/** Accent colour per project, echoing its card on the home page. */
export const ACCENTS = {
  "version3-omni-channel": "#4f46e5",
  "errum-online-store": "#ff5a1f",
  "version3-ai-support-agent": "#7c5cff",
  "listenr-for-bkash": "#e2136e",
  "adlead-analyzer": "#0ea5e9",
  "version3-website-hiring-suite": "#635bff",
  "rahiee-ai": "#16a34a",
};

export { prefersReducedMotion };

/**
 * Runs `setup(root, q)` inside a GSAP context scoped to the returned ref and
 * reverts everything on unmount. Skipped entirely for reduced-motion users,
 * whose CSS shows the final state.
 */
export function useGsap(setup, deps = []) {
  const ref = useRef(null);
  const setupRef = useRef(setup);
  // declared before the effect below, so each run sees this render's `setup`
  useLayoutEffect(() => {
    setupRef.current = setup;
  });

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || prefersReducedMotion()) return;
    const ctx = gsap.context(() => setupRef.current(root, gsap.utils.selector(root)), root);
    return () => ctx.revert();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return ref;
}

/** "5,600+" -> { to: 5600, comma: true, prefix: "", suffix: "+" }; null when it isn't a number. */
export function parseCount(value) {
  const m = /^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/.exec(value);
  if (!m || m[3].startsWith("/") || m[1].length > 1) return null;
  const raw = m[2].replace(/,/g, "");
  return { to: Number(raw), decimals: raw.includes(".") ? raw.split(".")[1].length : 0, comma: m[2].includes(","), prefix: m[1], suffix: m[3] };
}

export function formatCount(n, p) {
  const fixed = n.toFixed(p.decimals);
  const body = p.comma ? Number(fixed).toLocaleString("en-US", { minimumFractionDigits: p.decimals }) : fixed;
  return p.prefix + body + p.suffix;
}

/** Pulls an element toward the pointer while hovered (desktop only). */
export function useMagnetic(strength = 0.35) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.matchMedia("(hover: hover)").matches || prefersReducedMotion()) return;
    const xTo = gsap.quickTo(el, "x", { duration: 0.6, ease: "elastic.out(1, 0.45)" });
    const yTo = gsap.quickTo(el, "y", { duration: 0.6, ease: "elastic.out(1, 0.45)" });
    const move = (e) => {
      const r = el.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * strength);
      yTo((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const leave = () => {
      xTo(0);
      yTo(0);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerleave", leave);
    return () => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [strength]);
  return ref;
}
